import { useEffect, useState } from 'react';
import { useFigureCompositionStore, type JournalTemplateId } from '@/store/figure-composition';
import { FigureCanvas } from './FigureCanvas';
import { TemplatePresetSelector } from './TemplatePresetSelector';
import { PanelControls } from './PanelControls';
import { FigureTitle } from './FigureTitle';
import { FigureCaption } from './FigureCaption';
import { LegendControls } from './LegendControls';
import { GridSettings } from './GridSettings';
import { ExportDialog } from './ExportDialog';
import { journalTemplatePresets } from '@/lib/journalTemplates';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Loader2, AlertCircle, Info } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { FIGURE_TEMPLATE_PRESETS } from '@/lib/figureTemplates';

interface FigureCompositionEditorProps {
  projectId?: string;
}

export function FigureCompositionEditor({ projectId = 'dev-project' }: FigureCompositionEditorProps) {
  const currentComposition = useFigureCompositionStore((state) => state.currentComposition);
  const isLoading = useFigureCompositionStore((state) => state.isLoading);
  const error = useFigureCompositionStore((state) => state.error);
  const createComposition = useFigureCompositionStore((state) => state.createComposition);
  const setTheme = useFigureCompositionStore((state) => state.setTheme);
  const setCanvasSize = useFigureCompositionStore((state) => state.setCanvasSize);
  const [exportOpen, setExportOpen] = useState(false);
  const { toast } = useToast();

  // Show toast when error changes
  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: error,
        variant: 'destructive',
      });
    }
  }, [error, toast]);

  // Note: Composition creation/loading is handled by the parent page component
  // This component just renders the editor when currentComposition is available

  if (isLoading && !currentComposition) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading figure...</span>
      </div>
    );
  }

  if (!currentComposition) {
    return (
      <Card className="p-12 text-center">
        <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No figure loaded</h3>
        <p className="text-muted-foreground">
          {error || 'Please select a figure to edit.'}
        </p>
      </Card>
    );
  }

  const handleJournalTemplateChange = (id: JournalTemplateId | 'default') => {
    try {
      // Handle default option - just clear the journal template
      if (!id || id === 'default') {
        setTheme({
          journalTemplate: undefined,
        });
        return;
      }
      
      const template = journalTemplatePresets.find((t) => t.id === id);
      if (!template) {
        console.error(`Journal template "${id}" not found`);
        return;
      }

      if (!currentComposition) {
        console.error('No current composition');
        return;
      }

      const typography = template.typography ?? {};
      let fontFamily = 'Arial';
      if (Array.isArray(typography.fontFamily) && typography.fontFamily.length > 0) {
        fontFamily = typography.fontFamily[0];
      } else if (typeof typography.fontFamily === 'string' && typography.fontFamily.length > 0) {
        fontFamily = typography.fontFamily;
      }

      // Scale font size for screen readability (same scale factor as applyJournalTemplate)
      const SCREEN_SCALE_FACTOR = 1.8;
      const fontSizePt =
        typeof typography.fontSizePt_min === 'number'
          ? typography.fontSizePt_min
          : Array.isArray(typography.fontSizePt_range) && typography.fontSizePt_range.length > 0
          ? typography.fontSizePt_range[0]
          : 8;
      // Scale print sizes up for screen: convert pt to px and apply scale factor
      const fontSize = Math.max(10, Math.round(fontSizePt * SCREEN_SCALE_FACTOR));

      let colorMode: 'RGB' | 'CMYK' = 'RGB';
      if (template.colorPolicy?.mode === 'CMYK' || template.colorPolicy?.mode === 'CMYK_or_RGB') {
        colorMode = 'CMYK';
      } else if (template.colorPolicy?.mode === 'RGB') {
        colorMode = 'RGB';
      }

      setTheme({
        journalTemplate: id,
        fontFamily,
        fontSize,
        colorMode,
      });

      const sizePolicy = template.sizePolicy;
      if (sizePolicy) {
        if (sizePolicy.mode === 'columns' && Array.isArray(sizePolicy.widths_mm) && sizePolicy.widths_mm.length > 0) {
          const widthMm = Math.max(...sizePolicy.widths_mm);
          const heightMm = sizePolicy.maxHeight_mm ?? 234;
          setCanvasSize({ width: widthMm, height: heightMm, unit: 'mm' });
        } else if (sizePolicy.mode === 'range' && Array.isArray(sizePolicy.width_mm_range) && sizePolicy.width_mm_range.length === 2) {
          const widthMm = sizePolicy.width_mm_range[1];
          const heightMm = sizePolicy.maxHeight_mm ?? 234;
          setCanvasSize({ width: widthMm, height: heightMm, unit: 'mm' });
        } else if (sizePolicy.mode === 'presets' && sizePolicy.presets_cm?.large) {
          const [wCm, hCm] = sizePolicy.presets_cm.large;
          setCanvasSize({ width: wCm * 10, height: hCm * 10, unit: 'mm' });
        }
      }
    } catch (error) {
      console.error('Failed to apply journal template:', error);
    }
  };

  return (
    <div className="w-full h-full flex flex-col p-4">
      {/* Error banner */}
      {error && (
        <Card className="p-4 mb-4 border-destructive bg-destructive/10">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-destructive mb-1">Error</p>
              <p className="text-sm text-destructive">{error}</p>
            </div>
          </div>
        </Card>
      )}

      <div className="mb-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Multi-panel Figure</h1>
          <Button onClick={() => setExportOpen(true)} disabled={!currentComposition || isLoading}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
        <div className="flex flex-wrap gap-4 items-start mb-4">
          <div className="flex flex-col gap-2 min-w-[240px]">
            <FigureTitle />
            <div className="space-y-1">
              <Label className="text-sm text-muted-foreground">Journal template</Label>
              <Select
                value={currentComposition.theme.journalTemplate ?? 'default'}
                onValueChange={(value) => handleJournalTemplateChange(value as JournalTemplateId | 'default')}
              >
                <SelectTrigger className="mt-1 h-10">
                  <SelectValue placeholder="Default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  {journalTemplatePresets.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name ?? t.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <LegendControls />
            <GridSettings />
          </div>
          <div className="flex flex-col gap-2 min-w-[240px]">
            <FigureCaption />
            <TemplatePresetSelector />
            <PanelControls />
          </div>
        </div>
      </div>
      {/* Max panels warning */}
      {currentComposition.panels.length >= 6 && (
        <Card className="p-3 mb-4 border-amber-200 bg-amber-50">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-amber-600 mt-0.5" />
            <p className="text-sm text-amber-900">
              You've reached the maximum number of panels (6) for this figure in V1.
            </p>
          </div>
        </Card>
      )}

      {/* Empty state */}
      {currentComposition.panels.length === 0 && (
        <Card className="p-12 text-center border-dashed">
          <div className="max-w-md mx-auto">
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-lg font-semibold mb-2">No panels yet</h3>
            <p className="text-muted-foreground mb-4">
              Add panels to your figure or apply a template preset to get started.
            </p>
            <p className="text-sm text-muted-foreground">
              Use the controls above to add panels or select a template.
            </p>
          </div>
        </Card>
      )}

      <div className="flex-1 overflow-auto border rounded-md p-2 min-h-[400px]">
        {currentComposition.panels.length > 0 && (
          <FigureCanvas composition={currentComposition} />
        )}
      </div>
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} />
    </div>
  );
}
